import {
  Avatar,
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  SxProps,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  handleAddRemoveChoices,
  handleBasicsChange,
  handleBasicsSwitchChange,
  handleChoiceModification,
  handleTypeChange,
} from "../../../../Redux/reducers/consentSlice";
import { useAppDispatch, useAppSelector } from "../../../../Redux/hooks";
import { useCallback, useState } from "react";

import { Add, Delete } from "@mui/icons-material";

import { useParams } from "react-router-dom";
import { errorToastMessage, toastMessage } from "../../../../utils/toast";
import { consentBuilderGetUploadUrl } from "../../../../utils/upload";
import { useDropzone } from "react-dropzone";
import {
  previewWrapper,
  uploadWrapper,
} from "../../../QuestionBuilder/QuestionProperties/BasicTab";
import CMSTipTap from "../../../Tiptap/CMSTipTap";

type Props = {
  isEdit: boolean;
  block: any;
};

export const requiredStyles: SxProps = {
  "&::after": {
    content: '"*"',
    color: "red",
    marginLeft: "3px",
  },
};

export const BasicTab = ({ isEdit, block }: Props) => {
  const { id: studyId } = useParams();
  const formId = useAppSelector(({ consent }) => consent.formId);
  const type = useAppSelector((state) => state.consent.type);
  const versionId = useAppSelector((state) => state.consent.versionId);
  const formType = useAppSelector((state) => state.consent.formType);
  const dispatch = useAppDispatch();
  const isBasicError = useAppSelector(({ consent }) => consent.isBasicError);
  const isPreview = useAppSelector(({ consent }) => !consent.editable);
  const { choices }: { choices: any[] } = block?.config || [];

  const [loading, setLoading] = useState(false);

  const previewUrl = block?.signedResourceUrl;

  const onChange = (key: string, value: string | boolean) => {
    if (!isPreview)
      dispatch(
        handleBasicsChange({
          key,
          value,
        })
      );
  };
  const handleTipTapChange = useCallback(
    (data: string) => {
      if (!isPreview) {
        dispatch(
          handleBasicsChange({
            key: "label",
            value: data,
          })
        );
      }
    },
    [dispatch, isPreview]
  );

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      try {
        const file = acceptedFiles?.[0];
        if (file) {
          if (file.size > 5 * 1024 * 1024) {
            toastMessage("warning", "File Size cannot be greater than 5 MB!");
            return;
          }

          setLoading(true);

          const obj = await consentBuilderGetUploadUrl(
            file,
            studyId,
            formId,
            versionId,
            type,
            formType
          );

          if (obj.postUploadImageUrl) {
            dispatch(
              handleBasicsChange({
                key: "resourceUrl",
                value: obj.postUploadImageUrl,
              })
            );
          }
          if (obj.previewUrl) {
            dispatch(
              handleBasicsChange({
                key: "signedResourceUrl",
                value: obj.previewUrl,
              })
            );
          }

          setLoading(false);
        }
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    },
    [dispatch, formId, studyId, type, versionId, formType]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    accept:
      block.type === "picture"
        ? { "image/jpg": [], "image/jpeg": [], "image/png": [] }
        : { "video/*": [] },
    disabled: isPreview,
  });

  const removeImage = () => {
    dispatch(handleBasicsChange({ key: "resourceUrl", value: null }));
    dispatch(handleBasicsChange({ key: "signedResourceUrl", value: null }));
  };
  const onSwitch = (key: string, event: any) => {
    dispatch(handleBasicsSwitchChange({ key, value: event.target.checked }));
  };

  return (
    <Stack gap={2} py={1}>
      {isBasicError && (
        <Typography variant="caption" fontWeight="medium" color={"red"}>
          {isBasicError}
        </Typography>
      )}

      {["picture", "video"].includes(block.type) && (
        <Stack gap={1}>
          {loading ? (
            <Box sx={{ color: "text.secondary" }}>
              <CircularProgress />
            </Box>
          ) : previewUrl ? (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Tooltip
                title={
                  block.type === "picture" ? "Upload Image" : "Upload Video"
                }
                placement="right"
              >
                <Box
                  {...getRootProps({ className: "dropzone" })}
                  sx={previewWrapper}
                >
                  <input {...getInputProps()} />
                  {block.type === "picture" && (
                    <Avatar
                      src={previewUrl}
                      variant="rounded"
                      sx={{ width: 200, height: 140 }}
                    />
                  )}
                  {block.type === "video" && (
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: "regular",
                        color: "text.secondary",
                      }}
                    >
                      Video Uploaded
                    </Typography>
                  )}
                </Box>
              </Tooltip>
              {!isPreview && (
                <IconButton
                  color={"error"}
                  sx={{ height: 40, width: 40 }}
                  onClick={removeImage}
                >
                  <Delete />
                </IconButton>
              )}
            </Box>
          ) : (
            <Stack
              direction={"row"}
              alignItems={"center"}
              gap={1}
              {...getRootProps({ className: "dropzone" })}
              sx={uploadWrapper}
            >
              <input {...getInputProps()} />

              <Stack alignItems={"center"} gap={1}>
                <Add fontSize="medium" sx={{ color: "text.secondary" }} />
                <Typography
                  sx={{ color: "text.secondary" }}
                  variant="body1"
                  fontWeight={"medium"}
                  textAlign={"center"}
                >
                  Add {block.type === "picture" ? "Image" : "Video"} <br />
                  <Typography
                    sx={{ color: "text.secondary" }}
                    component={"span"}
                    fontSize={14}
                    fontWeight={400}
                  >
                    or drag and drop
                  </Typography>
                </Typography>
              </Stack>
            </Stack>
          )}
        </Stack>
      )}

      {["text"].includes(block.type) && (
        <Stack gap={1}>
          <Stack direction={"row"} justifyContent={"space-between"}>
            <Typography
              variant="body1"
              fontWeight={"regular"}
              sx={requiredStyles}
            >
              Label
            </Typography>
          </Stack>

          <CMSTipTap initialValue={block.label} onChange={handleTipTapChange} />
        </Stack>
      )}
      {!["text"].includes(block.type) && (
        <Stack gap={1}>
          <Typography
            variant="body1"
            fontWeight={"regular"}
            sx={
              block.type === "picture" || block.type === "video"
                ? {}
                : requiredStyles
            }
          >
            Label
          </Typography>
          <TextField
            name="label"
            fullWidth
            placeholder="Label"
            value={block.label}
            onChange={(e) => onChange("label", e.target.value)}
            InputProps={{
              readOnly: isPreview,
            }}
          />
        </Stack>
      )}

      {["picture"].includes(block.type) && (
        <Stack gap={1}>
          <Typography variant="body1" fontWeight={"regular"}>
            Alignment
          </Typography>
          <FormControl fullWidth>
            <Select
              readOnly={isPreview}
              fullWidth
              id="type"
              value={block?.config?.position}
              onChange={(e) =>
                dispatch(
                  handleBasicsSwitchChange({
                    key: "position",
                    value: e.target.value,
                  })
                )
              }
            >
              <MenuItem value={"left"}>Left</MenuItem>
              <MenuItem value={"center"}>Center</MenuItem>
              <MenuItem value={"right"}>Right</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      )}

      {["checkbox", "input"].includes(block.type) && (
        <Stack gap={2}>
          <FormControlLabel
            disabled={isPreview}
            control={
              <Switch
                checked={block?.config?.optional}
                onChange={(e) => onSwitch("optional", e)}
              />
            }
            label={
              <Typography variant="body1" fontWeight={"regular"}>
                Optional
              </Typography>
            }
          />
        </Stack>
      )}
      {["signature"].includes(block.type) && (
        <Stack gap={2}>
          <FormControlLabel
            disabled={isPreview}
            control={
              <Switch
                checked={block?.config?.name}
                onChange={(e) => onSwitch("name", e)}
              />
            }
            label={
              <Typography variant="body1" fontWeight={"regular"}>
                Name
              </Typography>
            }
          />

          <FormControlLabel
            disabled={isPreview}
            control={
              <Switch
                checked={block?.config?.date}
                onChange={(e) => onSwitch("date", e)}
              />
            }
            label={
              <Typography variant="body1" fontWeight={"regular"}>
                Date
              </Typography>
            }
          />

          <FormControlLabel
            disabled={isPreview}
            control={
              <Switch
                checked={block?.config?.address}
                onChange={(e) => onSwitch("address", e)}
              />
            }
            label={
              <Typography variant="body1" fontWeight={"regular"}>
                Address
              </Typography>
            }
          />

          <FormControlLabel
            disabled={isPreview}
            control={
              <Switch
                checked={block?.config?.provider || false}
                onChange={(e) => onSwitch("provider", e)}
              />
            }
            label={
              <Typography variant="body1" fontWeight={"regular"}>
                Provider Signature
              </Typography>
            }
          />

          {block?.config?.date && (
            <Stack gap={1}>
              <Typography variant="body1" fontWeight={"regular"}>
                Date Format
              </Typography>
              <FormControl fullWidth>
                <Select
                  readOnly={isPreview}
                  fullWidth
                  id="format"
                  value={block?.config?.format}
                  onChange={(e) =>
                    dispatch(
                      handleBasicsSwitchChange({
                        key: "format",
                        value: e.target.value,
                      })
                    )
                  }
                >
                  <MenuItem value={"DD/MM/YYYY"}>DD/MM/YYYY</MenuItem>
                  <MenuItem value={"MM/DD/YYYY"}>MM/DD/YYYY</MenuItem>
                  <MenuItem value={"DD/MM/YYYY HH:mm"}>
                    DD/MM/YYYY HH:mm
                  </MenuItem>
                  <MenuItem value={"MM/DD/YYYY HH:mm"}>
                    MM/DD/YYYY HH:mm
                  </MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}
        </Stack>
      )}

      {["input"].includes(block.type) && (
        <>
          <Stack gap={1}>
            <Typography variant="body1" fontWeight={"regular"}>
              Type
            </Typography>
            <FormControl fullWidth>
              <Select
                readOnly={isPreview}
                fullWidth
                id="type"
                value={block?.config?.type}
                onChange={(e) =>
                  dispatch(
                    handleTypeChange({
                      value: e.target.value,
                    })
                  )
                }
              >
                <MenuItem value={"text"}>Text</MenuItem>
                <MenuItem value={"date"}>Date</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <Stack gap={1}>
            <Typography variant="body1" fontWeight={"regular"}>
              Label Position
            </Typography>
            <FormControl fullWidth>
              <Select
                readOnly={isPreview}
                fullWidth
                id="type"
                value={block?.config?.position}
                onChange={(e) =>
                  dispatch(
                    handleBasicsSwitchChange({
                      key: "position",
                      value: e.target.value,
                    })
                  )
                }
              >
                <MenuItem value={"top"}>Top</MenuItem>
                <MenuItem value={"bottom"}>Bottom</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </>
      )}
      {["input"].includes(block.type) && block.config.type === "date" && (
        <Stack gap={1}>
          <Typography variant="body1" fontWeight={"regular"}>
            Date Format
          </Typography>
          <FormControl fullWidth>
            <Select
              readOnly={isPreview}
              fullWidth
              id="format"
              value={block?.config?.format}
              onChange={(e) =>
                dispatch(
                  handleBasicsSwitchChange({
                    key: "format",
                    value: e.target.value,
                  })
                )
              }
            >
              <MenuItem value={"DD/MM/YYYY"}>DD/MM/YYYY</MenuItem>
              <MenuItem value={"MM/DD/YYYY"}>MM/DD/YYYY</MenuItem>
              <MenuItem value={"DD/MM/YYYY HH:mm"}>DD/MM/YYYY HH:mm</MenuItem>
              <MenuItem value={"MM/DD/YYYY HH:mm"}>MM/DD/YYYY HH:mm</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      )}
      {["input"].includes(block.type) && block.config.type === "text" && (
        <Stack gap={1}>
          <Stack gap={2}>
            <FormControlLabel
              disabled={isPreview}
              control={
                <Switch
                  checked={block?.config?.allowMultiline}
                  onChange={(e) => onSwitch("allowMultiline", e)}
                />
              }
              label={
                <Typography variant="body1" fontWeight={"regular"}>
                  Allow Multiline
                </Typography>
              }
            />
          </Stack>
        </Stack>
      )}
      {["radio"].includes(block.type) && (
        <Stack gap={1}>
          <Typography variant="body1" fontWeight={"regular"}>
            Choice Values
          </Typography>
          {choices &&
            choices.map((choice, index) => (
              <Stack
                key={index}
                direction="row"
                alignItems={"center"}
                mb={2}
                gap={2}
              >
                <Box sx={{ flex: 1, minWidth: "1px" }}>
                  <TextField
                    fullWidth
                    placeholder={`Key ${index + 1}`}
                    value={choice.key}
                    onChange={(e) =>
                      dispatch(
                        handleChoiceModification({
                          index: index,
                          section: "key",
                          value: e.target.value,
                        })
                      )
                    }
                    name="option-label"
                    InputProps={{
                      readOnly: isPreview,
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: "1px", alignSelf: "flex-end" }}>
                  <TextField
                    fullWidth
                    multiline
                    placeholder={`Label ${index + 1}`}
                    value={choice.label}
                    onChange={(e) =>
                      dispatch(
                        handleChoiceModification({
                          index: index,
                          section: "label",
                          value: e.target.value,
                        })
                      )
                    }
                    name="option-value"
                    InputProps={{
                      readOnly: isPreview,
                    }}
                  />
                </Box>
                {!isPreview && (
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    gap={1}
                    sx={{ width: "50px" }}
                  >
                    <IconButton
                      aria-label="add"
                      size="small"
                      onClick={() =>
                        dispatch(
                          handleAddRemoveChoices({
                            actionType: "add",
                            actionIndex: index,
                          })
                        )
                      }
                      color="primary"
                    >
                      <Add fontSize="small" />
                    </IconButton>

                    <IconButton
                      aria-label="delete"
                      size="small"
                      color="error"
                      onClick={() =>
                        dispatch(
                          handleAddRemoveChoices({
                            actionType: "remove",
                            actionIndex: index,
                          })
                        )
                      }
                      disabled={choices.length === 1}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                )}
              </Stack>
            ))}
        </Stack>
      )}
    </Stack>
  );
};
